import { graphql } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type {
	preloadOutboundBylineSsrStateQuery$data,
	preloadOutboundBylineSsrStateQuery$variables,
} from './__generated__/preloadOutboundBylineSsrStateQuery.graphql.ts';
import {
	createOutboundBylineSsrState,
	normalizeOutboundBylineSsrState,
	type OutboundBylineSsrState,
} from './outboundBylineSsrState.ts';

const OUTBOUND_BYLINE_PRELOAD_TIMEOUT_MS = 300;
const OUTBOUND_BYLINE_PRELOAD_TIMED_OUT = Symbol('outbound-byline-preload-timed-out');

type OutboundBylineUnavailableReason = 'malformed-response' | 'query-error' | 'timeout';
type OutboundBylineUnavailableCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';
type PreloadOutgoingLinksResult = Awaited<ReturnType<typeof preloadOutgoingLinks>>;

const OutboundBylineSsrStateQuery = graphql`
	query preloadOutboundBylineSsrStateQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				outgoingLinks {
					externalOutgoingLinks(first: 3) {
						nodes {
							url
						}
					}
					internalOutgoingLinks(first: 3) {
						nodes {
							id
							links {
								base
								webui
							}
						}
					}
				}
			}
		}
	}
`;

const preloadOutgoingLinks = (contentId: string) =>
	query<preloadOutboundBylineSsrStateQuery$data, preloadOutboundBylineSsrStateQuery$variables>({
		query: OutboundBylineSsrStateQuery,
		variables: { contentId },
	});

const isNullOrUndefined = (value: unknown): value is null | undefined => {
	if (value === null) {
		return true;
	}
	return value === undefined;
};

const getUnavailableCauseCategory = (cause?: unknown): OutboundBylineUnavailableCauseCategory => {
	if (isNullOrUndefined(cause)) {
		return 'none';
	}
	if (!(cause instanceof Error)) {
		return 'non-error';
	}
	if (cause.name === 'GraphQLError') {
		return 'graphql';
	}
	return 'error';
};

const reportUnavailable = (reason: OutboundBylineUnavailableReason, cause?: unknown): void => {
	const causeCategory = getUnavailableCauseCategory(cause);

	ssrPrepareErrorsClient.submitError(
		new Error(`Outbound byline SSR state unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-outbound-byline-ssr-state',
			reason,
			causeCategory,
		},
	);

	if (cause instanceof Error) {
		markErrorAsHandled(cause);
	}
};

const normalizeOutgoingLinks = (
	contentId: string,
	result: PreloadOutgoingLinksResult,
): OutboundBylineSsrState => {
	if (result.hasErrors || result.errors?.length) {
		reportUnavailable('query-error', result.errors?.[0]);
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}

	const state = normalizeOutboundBylineSsrState(contentId, result.data);
	if (state.status === 'unavailable') {
		reportUnavailable('malformed-response');
	}
	return state;
};

export const preloadOutboundBylineSsrState = async (
	contentId: string,
): Promise<OutboundBylineSsrState> => {
	try {
		const outgoingLinksPromise = preloadOutgoingLinks(contentId);
		const result = await timeGuard<
			PreloadOutgoingLinksResult | typeof OUTBOUND_BYLINE_PRELOAD_TIMED_OUT
		>(
			() => outgoingLinksPromise,
			OUTBOUND_BYLINE_PRELOAD_TIMED_OUT,
			OUTBOUND_BYLINE_PRELOAD_TIMEOUT_MS,
		);

		if (result === OUTBOUND_BYLINE_PRELOAD_TIMED_OUT) {
			reportUnavailable('timeout');
			return createOutboundBylineSsrState(contentId, 'unavailable');
		}

		return normalizeOutgoingLinks(contentId, result);
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return createOutboundBylineSsrState(contentId, 'unavailable');
	}
};
